import { VStack, Box, Button, HStack, Checkbox, FormSpacer } from '@cad-arc';
import { BooleanValueOption, EventSearchOptions } from '@mark43/cad-api';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { FormSection } from '../search-windows/form-section';
import { EventSearchFormType } from './types';
import { vehicleFormDefaultValues } from '../search-windows/configuration';
import { SearchFormVehicleFields } from '../search-windows/search-form-vehicle-fields';
import { useEnabledFeatures } from '~/common/hooks/enabled-features';

const MAX_VEHICLE_COUNT = 3;

export function EventSearchFormVehicleInformationSection({
    form,
    options,
    isFirstResponder,
}: {
    form: UseFormReturn<EventSearchFormType>;
    options: EventSearchOptions;
    isFirstResponder?: boolean;
}) {
    const { fields, append, remove } = useFieldArray({ control: form.control, name: 'vehicles' });
    const enabledFeatures = useEnabledFeatures();
    const hasHeldTowVehicle = form.watch('hasHeldTowVehicle');
    const hasReleasedTowVehicle = form.watch('hasReleasedTowVehicle');

    const handleHeldTowVehicleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        const nextValue: BooleanValueOption | null = checked
            ? { label: 'Held', value: true }
            : null;
        form.setValue('hasHeldTowVehicle', nextValue, {
            shouldDirty: true,
            shouldValidate: false,
        });
    };
    const handleReleasedTowVehicleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        const nextValue: BooleanValueOption | null = checked
            ? { label: 'Released', value: true }
            : null;
        form.setValue('hasReleasedTowVehicle', nextValue, {
            shouldDirty: true,
            shouldValidate: false,
        });
    };

    return (
        <FormSection title="Vehicle Information" testId="event-search-vehicle-information-section">
            <VStack align="left" gap="4px">
                {fields.map((field, index) => {
                    return (
                        <SearchFormVehicleFields
                            key={field.id}
                            options={options}
                            index={index}
                            isFirstResponder={isFirstResponder}
                            isRemovable={fields.length > 1}
                            onRemove={() => remove(index)}
                        />
                    );
                })}

                {enabledFeatures.enhancedServiceRotation ? (
                    <VStack align="left">
                        <HStack justify="flex-start" spacing={4}>
                            <Box>
                                <FormSpacer alignItems="center">
                                    <Checkbox
                                        isChecked={hasHeldTowVehicle?.value === true}
                                        onChange={handleHeldTowVehicleChange}
                                    >
                                        Held
                                    </Checkbox>
                                </FormSpacer>
                            </Box>
                            <Box>
                                <FormSpacer alignItems="center">
                                    <Checkbox
                                        isChecked={hasReleasedTowVehicle?.value === true}
                                        onChange={handleReleasedTowVehicleChange}
                                    >
                                        Released
                                    </Checkbox>
                                </FormSpacer>
                            </Box>
                            <Button
                                variant="ghost"
                                onClick={() => {
                                    form.setValue(`hasHeldTowVehicle`, null);
                                    form.setValue(`hasReleasedTowVehicle`, null);
                                }}
                            >
                                Clear
                            </Button>
                        </HStack>
                    </VStack>
                ) : null}
                <Box w="122px">
                    <Button
                        variant="ghost"
                        leadingVisual="Add"
                        onClick={() => append(vehicleFormDefaultValues)}
                        isDisabled={fields.length >= MAX_VEHICLE_COUNT}
                    >
                        Add Vehicle
                    </Button>
                </Box>
            </VStack>
        </FormSection>
    );
}
